import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import i18n from "../i18n/i18n";

import { ReportEntry } from "../@types/ReportEntry";
import Collaborator from "../@types/Collaborator";
import { filterToParams } from "../utils/filter";
import { FilterValues } from "../@types/filter";
import { api } from "./api";
import { useNavigate } from "react-router-dom";

export type FilterReportEntries = {
  content: ReportEntry[];
  size: number;
  totalElements: number;
  totalPages: number;
};

export type FilterReportEntriesTotalsByCollaborators = {
  content: Collaborator[];
  size: number;
  totalElements: number;
  totalPages: number;
  name?: string;
  status?: string;
};

export type FilterReportEntryApprovals = {
  content: Collaborator[];
  size: number;
  totalElements: number;
  totalPages: number;
};

export const fetchReportEntries = async (
  page?: number,
  size?: number,
  sort?: string,
  filters?: FilterValues,
): Promise<FilterReportEntries> => {
  const response = await api.get<FilterReportEntries>(`/report_entries`, {
    params: filterToParams(filters!, page!, size!, sort!),
  });
  return response.data;
};

export const exportReportEntries = async (
  filters?: FilterValues,
  exportSheet?: boolean,
  exportFiles?: boolean,
): Promise<number> => {
  const response = await api.get(`/report_entries/export`, {
    params: { ...filterToParams(filters!), exportSheet: exportSheet, exportFiles: exportFiles },
  });
  return response.status;
};

export const fetchReportEntriesTotalByCollaborator = async (
  page?: number,
  size?: number,
  filters?: FilterValues,
): Promise<FilterReportEntriesTotalsByCollaborators> => {
  const response = await api.get<FilterReportEntriesTotalsByCollaborators>(`/report_entries/totals/by_collaborator`, {
    params: filterToParams(filters!, page!, size!),
  });

  return response.data;
};

export const fetchReportEntryApprovals = async (
  page?: number,
  size?: number,
  filters?: FilterValues,
): Promise<FilterReportEntryApprovals> => {
  const response = await api.get<FilterReportEntryApprovals>(`/report_entries/approvals`, {
    params: filterToParams(filters!, page!, size!),
  });

  return response.data;
};

export const fetchReportEntryById = async (id: number) => {
  const response = await api.get<ReportEntry>(`/report_entries/${id}`);

  return response.data;
};

export type AdvancePaymentDetails = {
  reportEntry: ReportEntry;
  reportEntryId: number;
  isForCurrencyConversion?: boolean;
  targetCurrency?: string;
  requestedAmount?: number;
  additionalComments?: string;
};

export const fetchAdvancePayment = async (reportEntryId: number): Promise<AdvancePaymentDetails> => {
  const response = await api.get<AdvancePaymentDetails>(`/report_entries/${reportEntryId}/advance_payment`);
  return response.data;
};

export const addReportEntry = async (reportEntry: ReportEntry, attachedFiles: File[]): Promise<ReportEntry> => {
  const formData = new FormData();
  formData.append("report", new Blob([JSON.stringify(reportEntry)], { type: "application/json" }));
  formData.append("file", attachedFiles[0]);

  if (reportEntry.expendSubcategory == "") reportEntry.expendSubcategory = null;

  const response = await api.post<ReportEntry>(`/report_entries`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};

export const updateReportEntry = async (reportEntry: ReportEntry, attachedFiles?: File[]): Promise<ReportEntry> => {
  const formData = new FormData();
  formData.append("report", new Blob([JSON.stringify(reportEntry)], { type: "application/json" }));

  if (attachedFiles && attachedFiles.length > 0) {
    formData.append("file", attachedFiles[0]);
  }

  if (reportEntry.expendSubcategory == "") reportEntry.expendSubcategory = null;

  const response = await api.patch<ReportEntry>(`/report_entries`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
};

export const deleteReportEntry = async (id: number) => {
  const response = await api.delete<void>(`/report_entries/${id}`);
  return response.data;
};

export const downloadFile = async (reportEntryId: number) => {
  const response = await api.get(`/report_entries/${reportEntryId}/file`, {
    responseType: "blob",
  });

  return response.data;
};

export const sendToApproval = async (id: number): Promise<ReportEntry> => {
  const response = await api.patch<ReportEntry>(`/report_entries/${id}/send_to_approval`);
  return response.data;
};

export const sendToFinancialValidation = async (id: number): Promise<ReportEntry> => {
  const response = await api.patch<ReportEntry>(`/report_entries/${id}/send_to_financial_validation`);
  return response.data;
};

export const sendAllToFinancialValidation = async (filter: FilterValues) => {
  const response = await api.patch<number>(`/report_entries/send_all_to_financial_validation`, null, {
    params: filterToParams(filter),
  });

  return response.data;
};

export const sendToFinancialValidationById = async (id: number): Promise<number> => {
  const response = await api.patch<number>(`/report_entries/send_to_financial_validation/${id}`);
  return response.data;
};

export const sendAllToPaid = async (filter?: FilterValues): Promise<number> => {
  const response = await api.patch<number>(`/report_entries/send_all_to_paid`, null, {
    params: filter ? filterToParams(filter!) : undefined,
  });

  return response.data;
};

export const approvalsApprove = async (id: number, approveReason?: string, type?: string): Promise<ReportEntry> => {
  const response = await api.patch<ReportEntry>(`/report_entries/approve/${id}`, { reason: approveReason, type });
  return response.data;
};

export const approvalsApprovePerManager = async (id: number, approveReason?: string): Promise<ReportEntry> => {
  const response = await api.patch<ReportEntry>(`/report_entries/approveForManager/${id}`, { reason: approveReason });
  return response.data;
};

export const approvalsApproveAll = async (filter: FilterValues) => {
  const response = await api.patch<ReportEntry>("/report_entries/approve_all", null, {
    params: filterToParams(filter),
  });
  return response.data;
};

export const approvalsRefuse = async (id: number, refusedReason: string): Promise<ReportEntry> => {
  const response = await api.patch<ReportEntry>(`/report_entries/refuse/${id}`, { reason: refusedReason });
  return response.data;
};

export const reportPay = async (id: number): Promise<ReportEntry> => {
  const response = await api.patch<ReportEntry>(`/report_entries/${id}/mark_as_paid`);
  return response.data;
};

export const getPendingAdvances = async (filter: FilterValues) => {
  const response = await api.get<ReportEntry[]>("/report_entries/advances/pending", {
    params: filterToParams(filter),
  });
  return response.data;
};

export const useReportEntries = (
  page?: number,
  size?: number,
  filters?: FilterValues,
  shouldFetch: boolean = true,
  sort?: string,
) => {
  return useQuery({
    queryKey: ["report_entries", page, size, sort, filters],
    queryFn: () => fetchReportEntries(page, size, sort, filters),
    staleTime: 0,
    cacheTime: 0,
    enabled: shouldFetch,
  });
};

export const useReportEntryApprovals = (
  page?: number,
  size?: number,
  filters?: FilterValues,
  shouldFetch: boolean = true,
) => {
  return useQuery({
    queryKey: ["report_entries", "approvals", page, size, filters],
    queryFn: () => fetchReportEntryApprovals(page, size, filters),
    staleTime: 0,
    cacheTime: 0,
    enabled: shouldFetch,
  });
};

export const useApprovalsApprove = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, approveReason }: { id: number; approveReason?: string }) => {
      return approvalsApprove(id, approveReason);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report_entries"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(i18n.t("reportEntryService.approvals.managerSuccess"));
    },
    onError: (error: Error) => {
      toast.error(error.message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    },
  });
};

export const useApprovalsApprovePerManager = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, approveReason }: { id: number; approveReason?: string }) => {
      return approvalsApprovePerManager(id, approveReason);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report_entries"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(i18n.t("reportEntryService.approvals.managerSuccess"));
    },
    onError: (error: Error) => {
      toast.error(error.message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    },
  });
};

export const useApproveAll = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (filter: FilterValues) => {
      return approvalsApproveAll(filter);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report_entries"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(i18n.t("reportEntryService.approvals.managerSuccess"));
    },
    onError: (error: Error) => {
      toast.error(error.message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    },
  });
};

export const useSendAllToFinancialValidationById = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (filter: FilterValues) => {
      return sendAllToFinancialValidation(filter);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report_entries"] });
      toast.success(i18n.t("reportEntryService.financialValidation.success"));
    },
    onError: (error: Error) => {
      toast.error(error.message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    },
  });
};

export const useApprovalsRefuse = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, refuseReason }: { id: number; refuseReason: string }) => {
      if (!refuseReason) {
        throw new Error(i18n.t("reportEntryService.approvals.refuseReasonRequired"));
      }
      return approvalsRefuse(id, refuseReason);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report_entries"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(i18n.t("reportEntryService.approvals.refusedSuccess"));
    },
    onError: (error: Error) => {
      toast.error(error.message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    },
  });
};

export const useSendToApproval = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => sendToApproval(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report_entries"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(i18n.t("reportEntryService.approvals.success"));
    },
    onError: (error: Error) => {
      toast.error(error.message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    },
  });
};

export const markAdvanceAsPaid = async (reportEntryId: number): Promise<ReportEntry> => {
  const response = await api.patch<ReportEntry>(`/report_entries/${reportEntryId}/mark_as_paid`);
  return response.data;
};

export const confirmAdvanceReceipt = async (confirmationData: any): Promise<ReportEntry> => {
  const formData = new FormData();

  Object.keys(confirmationData).forEach((key) => {
    if (key === "confirmationProof" && confirmationData[key]) {
      formData.append("confirmationProof", confirmationData[key]);
    } else if (confirmationData[key] !== undefined && confirmationData[key] !== null) {
      formData.append(key, confirmationData[key]);
    }
  });

  const response = await api.patch<ReportEntry>(
    `/report_entries/${confirmationData.reportEntryId}/confirm_receipt`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

export const useReportPay = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => reportPay(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report_entries"] });
      toast.success(i18n.t("reportEntryService.payment.reportPaidSuccess"));
    },
    onError: (error: Error) => {
      toast.error(error.message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    },
  });
};

export const useMarkAdvanceAsPaid = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reportEntryId: number) => markAdvanceAsPaid(reportEntryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report_entries"] });
      toast.success(i18n.t("reportEntryService.payment.advancePaidSuccess"));
    },
    onError: (error: Error) => {
      toast.error(error.message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    },
  });
};

export const useConfirmAdvanceReceipt = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (confirmationData: any) => confirmAdvanceReceipt(confirmationData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report_entries"] });
      toast.success(i18n.t("reportEntryService.advanceReceipt.success"));
    },
    onError: (error: Error) => {
      toast.error(error.message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    },
  });
};

export const useSendAllToPaid = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (filter?: FilterValues) => sendAllToPaid(filter),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report_entries"] });
      toast.success(i18n.t("reportEntryService.payment.reportsPaidSuccess"));
    },
    onError: (error: Error) => {
      toast.error(error.message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    },
  });
};

export const useExportReportEntriesSheet = () => {
  return useMutation({
    mutationFn: ({ filters }: { filters: FilterValues | null }) =>
      exportReportEntries(filters || undefined, true, false),
    onSuccess: () => {
      toast.success(i18n.t("refundReportTableButtons.export.sheet.success"));
    },
    onError: (error: AxiosError<string>) => {
      if (error.response?.status === 409) {
        toast.error(i18n.t("refundReportTableButtons.exportInProgress"), {
          position: toast.POSITION.TOP_RIGHT,
        });
        return;
      }

      toast.error(error.response?.data || error.message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    },
  });
};

export const useExportReportEntriesFiles = () => {
  return useMutation({
    mutationFn: ({ filters }: { filters: FilterValues | null }) =>
      exportReportEntries(filters || undefined, false, true),
    onSuccess: () => {
      toast.success(i18n.t("refundReportTableButtons.export.files.success"));
    },
    onError: (error: AxiosError<string>) => {
      if (error.response?.status === 409) {
        toast.error(i18n.t("refundReportTableButtons.exportInProgress"), {
          position: toast.POSITION.TOP_RIGHT,
        });
        return;
      }

      toast.error(error.response?.data || error.message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    },
  });
};

export const useGetPendingAdvances = (filter: FilterValues) => {
  return useQuery({
    queryKey: ["pending_advances", filter],
    queryFn: () => getPendingAdvances(filter),
    enabled: false,
    onError: (error: Error) => {
      toast.error(error.message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    },
  });
};

export const useDeleteReportEntry = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteReportEntry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report_entries"] });
      toast.success(i18n.t("reportEntryService.delete.success"));
      navigate("/");
    },
    onError: (error: Error) => {
      toast.error(error.message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    },
  });
};
